/**
 * Nodos y utilidades del editor enriquecido.
 *
 * Se separa del componente por dos razones: el archivo del editor queda
 * centrado en el contenido, y la lista de nodos se puede testear sin montar
 * React.
 */

import { Node } from "@tiptap/core";

/**
 * H1 esta deliberadamente ausente: el H1 de la pagina es el titulo de la
 * publicacion, y permitirlo dentro del cuerpo produciria dos H1 en el mismo
 * documento. El StarterKit se configura con `heading: { levels: [2, 3] }`.
 */
export const HEADING_LEVELS = [2, 3] as const;

/**
 * Bloque de FAQ.
 *
 * Es un node atomico: pregunta y respuesta viajan en `attrs`, no como
 * contenido. Asi el renderer puede extraerlas de forma fiable para el schema
 * FAQPage y la respuesta no queda a medio escribir dentro del cuerpo. El
 * drawback es que no se puede aplicar formato dentro de la respuesta; es
 * aceptable porque las respuestas de FAQ son cortas por definicion.
 */
export const FaqItem = Node.create({
  name: "faqItem",

  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      question: { default: "" },
      answer: { default: "" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-faq-item]",
        getAttrs: (element) => {
          const node = element as HTMLElement;
          return {
            question: node.getAttribute("data-question") ?? "",
            answer: node.getAttribute("data-answer") ?? "",
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "div",
      {
        ...HTMLAttributes,
        "data-faq-item": "true",
        "data-question": String(node.attrs.question ?? ""),
        "data-answer": String(node.attrs.answer ?? ""),
      },
    ];
  },
});

/** Contenedor de preguntas de FAQ. */
export const FaqList = Node.create({
  name: "faqList",

  group: "block",
  content: "faqItem+",
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: "div[data-faq-list]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", { ...HTMLAttributes, "data-faq-list": "true" }, 0];
  },
});

const SAFE_PROTOCOLS = ["http", "https", "mailto", "tel"];

/**
 * Primera barrera contra `javascript:` y `data:` pegados desde Word o Google
 * Docs. La segunda es `safeUrl` en @vimos/rich-content, en el renderer. Las dos
 * hacen falta: el editor evita que el dato llegue a existir, el renderer evita
 * que exista algo que se haya colado por otra via (importaciones, APIs).
 */
export function isSafeLinkTarget(href: string): boolean {
  const value = href.trim();
  if (!value) return false;
  if (value.startsWith("/")) return !value.startsWith("//");
  if (value.startsWith("#")) return true;
  // Control chars invisibles son la forma clasica de esconder un esquema.
  if (/[\u0000-\u001f\u007f]/.test(value)) return false;
  const match = /^([a-z][a-z0-9+.-]*):/i.exec(value);
  if (!match?.[1]) return false;
  return SAFE_PROTOCOLS.includes(match[1].toLowerCase());
}

/** Igual que `isSafeLinkTarget`, pero solo http/https y rutas internas. */
export function isSafeImageTarget(src: string): boolean {
  const value = src.trim();
  if (value.startsWith("/")) return !value.startsWith("//");
  return /^https?:/i.test(value);
}
